import { apiGet, apiPost, apiDelete, apiPostPublic, apiPatch } from './index';
import type { SignupFormData, OptionalProfileData } from '../types/onboarding';

export interface ThumbsUpData {
  receiver_id: string;
  chat_message_id: string;
}

export interface ThumbsUpResponse {
  success: boolean;
  message: string;
  helped_count?: number;
  already_given?: boolean;
}

export interface ThumbsUpStats {
  helped_count: number;
  thumbs_up_given: number;
}

// Registration request/response types
export interface RegisterUserRequest {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  visa_type: string;
  current_location: {
    city: string;
    state: string;
    country: string;
  };
}

export interface RegisterUserResponse {
  success: boolean;
  user: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    visa_type: string;
    current_location: {
      city: string;
      state: string;
    };
  };
  token: string;
  message?: string;
}

// Profile update request/response types
export interface UpdateProfileRequest {
  languages?: string[];
  primary_goal?: string;
  nationality?: string;
  hobbies?: string[];
  occupation?: string;
  employer?: string;
  date_of_birth?: string;
  time_in_us?: string;
  just_arrived?: boolean;
  arrival_date?: string;
  us_location_status?: 'in_us' | 'planning' | null;
}

export interface UpdateProfileResponse {
  success: boolean;
  user: {
    id: string;
    languages?: string[];
    primary_goal?: string;
  };
  message?: string;
}

class UserService {
  /**
   * Register a new user
   * @param data Signup form data
   * @returns Registration response with user data and token
   */
  async registerUser(data: SignupFormData): Promise<RegisterUserResponse> {
    // Parse location string (City, State)
    const [city, state] = data.location.split(',').map(part => part.trim());

    // Map to backend Expected format (snake_case)
    const requestData: Record<string, unknown> = {
      first_name: data.firstName,
      last_name: data.lastName,
      email: data.email,
      password: data.password,
      visa_type: data.visaType,
      current_location: {
        city: city || data.location,
        state: state || '',
        country: 'US', // Default to US for now as per app context
      },
      member_type: data.memberType || 'visa_worker',
    };

    // Community helper fields (only sent when relevant)
    if (data.memberType === 'community_helper') {
      if (data.visaHistory) requestData.visa_history = data.visaHistory;
      if (data.helperStatus) requestData.helper_status = data.helperStatus;
    }

    if (data.promoCode && data.promoCode.trim()) {
      requestData.promo_code = data.promoCode.trim();
    }

    if (data.dateOfBirth) {
      requestData.date_of_birth = data.dateOfBirth;
    }

    return apiPostPublic<RegisterUserResponse>('/api/auth/register', requestData);
  }

  /**
   * Update user profile with optional data
   * Uses authenticated endpoint - no userId needed as it uses the token
   * @param data Optional profile data (languages, primaryGoal)
   * @returns Updated user data
   */
  async updateUserProfile(
    data: OptionalProfileData & Partial<UpdateProfileRequest>
  ): Promise<UpdateProfileResponse> {
    const requestData: UpdateProfileRequest = {
      languages: data.languages,
      primary_goal: data.primaryGoal || undefined,
    };
    const passthrough: (keyof UpdateProfileRequest)[] = [
      'nationality', 'hobbies', 'occupation', 'employer',
      'time_in_us', 'just_arrived', 'arrival_date', 'us_location_status',
    ];
    for (const key of passthrough) {
      const value = (data as any)[key];
      if (value !== undefined) (requestData as any)[key] = value;
    }

    // Use PATCH /api/user/profile (authenticated endpoint)
    const response = await apiPatch<{ success: boolean; message: string; data: any }>(
      '/api/user/profile',
      requestData
    );

    return {
      success: response.success,
      user: response.data,
      message: response.message,
    };
  }
  // Give a thumbs-up to a user
  async giveThumbsUp(data: ThumbsUpData): Promise<ThumbsUpResponse> {
    return apiPost<ThumbsUpResponse>('/api/user/thumbs-up', data);
  }

  // Remove a thumbs-up from a user
  async removeThumbsUp(receiverId: string): Promise<ThumbsUpResponse> {
    return apiDelete<ThumbsUpResponse>(`/api/user/thumbs-up/${receiverId}`);
  }

  // Check if user has given a thumbs-up to another user
  async hasGivenThumbsUp(
    receiverId: string
  ): Promise<{ success: boolean; has_given_thumbs_up: boolean }> {
    const response = await apiGet<{
      success: boolean;
      has_given_thumbs_up: boolean;
    }>(`/api/user/thumbs-up/${receiverId}`);
    return response;
  }

  // Get thumbs-up statistics for a user
  async getThumbsUpStats(userId: string): Promise<ThumbsUpStats> {
    const response = await apiGet<{ success: boolean; data: ThumbsUpStats }>(
      `/api/user/${userId}/thumbs-up-stats`
    );
    return response.data;
  }
}

export const userService = new UserService();
